import React, { useCallback, useEffect, useReducer, useRef } from 'react'

import { useHistory } from 'components/ReactRouterProvider/context'

import { IAwesomeBarProps } from '../types'
import { AwesomeBarContext, useAwesomeBarContext } from './context'
import { useKeyUpWhileOpen } from './key-up/use-open-key-up'
import { INITIAL_STATE, hooks, reducer } from './reducer'

export { useAwesomeBarContext }

interface AwesomeBarProviderProps extends IAwesomeBarProps {
  children: React.ReactNode
}

/**
 * This is the component that wraps the entire AwesomeBar and provides
 * the context for all of the other components.
 */
export const AwesomeBarContextProvider = ({
  children,
  searchConfigurations,
  allResultsOrder,
  getEmptyMessage,
  focusDropdownSections,
}: AwesomeBarProviderProps) => {
  const history = useHistory()
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const {
    showFocusDropdown,
    showResults,
    showTabs,
    searchTerm,
    resultType,
    resultTypeIndex,
    results,
    loading,
    placeholderOptions,
    tabLabels,
    configs,
    configsByLabel,
  } = state

  useEffect(() => {
    onBlurInput()
    // Close the awesomebar when the URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history?.location.pathname])

  const inputRef = useRef<HTMLInputElement>(null)
  const onBlur = hooks.useOnBlur(dispatch)
  const onChange = hooks.useOnChange(dispatch)
  const onChangeResultType = hooks.useOnChangeResultType(dispatch)
  const onClear = hooks.useOnClear(dispatch)
  const onCloseResults = hooks.useOnCloseResults(dispatch)
  const onFocus = hooks.useOnFocus(dispatch)
  const onInputChange = hooks.useOnInputChange(dispatch, state)

  const onRefocusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  const onBlurInput = useCallback(() => {
    inputRef.current?.blur()
    onBlur()
  }, [onBlur])

  const onEscapeKey = useCallback(() => {
    inputRef.current?.blur()
    onClear()
  }, [onClear])

  useKeyUpWhileOpen(showResults || showFocusDropdown, {
    onEscape: onEscapeKey,
    onUp: onRefocusInput,
    onDown: onRefocusInput,
  })

  hooks.useInitialize(dispatch, searchConfigurations, allResultsOrder)

  return (
    <AwesomeBarContext.Provider
      value={{
        allResultsOrder,
        configs,
        configsByLabel,
        focusDropdownSections,
        getEmptyMessage,
        inputRef,
        loading,
        onBlur,
        onBlurInput,
        onChange,
        onChangeResultType,
        onCloseResults,
        onFocus,
        onInputChange,
        placeholderOptions,
        results,
        resultType,
        resultTypeIndex,
        searchTerm,
        showFocusDropdown,
        showResults,
        showTabs,
        tabLabels,
      }}
    >
      {children}
    </AwesomeBarContext.Provider>
  )
}
